import axios from 'axios';
import { isSupportTab, readSupportSession, endSupportTab } from './supportSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Do not set a global Content-Type. Axios must choose JSON for object payloads
// and let the browser add the multipart boundary for FormData uploads.
export const API_CLIENT_DEFAULTS = {
  baseURL: API,
  withCredentials: true,
};

const api = axios.create(API_CLIENT_DEFAULTS);

export const CANONICAL_TENANT_ID_PATTERN =
  /^[a-z][a-z0-9_-]*::[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const BROWSER_TENANT_STORAGE_KEY = 'aw_skills_tenant';
export const BROWSER_TENANT_CHANGED_EVENT = 'actionweave:tenant-changed';

const canonicalTenantId = (value) => (
  typeof value === 'string' && CANONICAL_TENANT_ID_PATTERN.test(value)
    ? value
    : null
);

/**
 * Synchronize browser scope from the authenticated server session.
 *
 * The session-selected tenant is authoritative. URL parameters and local
 * storage are navigation preferences only and must never keep a browser on a
 * tenant that is absent from the authenticated membership list.
 */
export const synchronizeBrowserTenantContext = (context) => {
  if (typeof window === 'undefined') return null;
  if (isSupportTab()) {
    const tenant = readSupportSession()?.tenant_id;
    if (!tenant || context?.active_tenant_id !== tenant || !context?.support_access) {
      endSupportTab();
      throw new Error('Support organization could not be verified. Open a new support session.');
    }
    return tenant; // Never mutate another tab's customer tenant preference.
  }

  const activeTenantId = canonicalTenantId(context?.active_tenant_id);
  const memberships = Array.isArray(context?.memberships) ? context.memberships : [];
  const authorizedTenantIds = new Set(
    memberships
      .map((membership) => canonicalTenantId(membership?.tenant_id))
      .filter(Boolean),
  );

  if (!activeTenantId || (
    authorizedTenantIds.size > 0 && !authorizedTenantIds.has(activeTenantId)
  )) {
    localStorage.removeItem(BROWSER_TENANT_STORAGE_KEY);
    return null;
  }

  const previousTenantId = localStorage.getItem(BROWSER_TENANT_STORAGE_KEY);
  localStorage.setItem(BROWSER_TENANT_STORAGE_KEY, activeTenantId);

  const url = new URL(window.location.href);
  const urlTenantId = url.searchParams.get('tenant_id');
  if (urlTenantId && urlTenantId !== activeTenantId) {
    url.searchParams.delete('tenant_id');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }

  if (previousTenantId !== activeTenantId) {
    window.dispatchEvent(new CustomEvent(BROWSER_TENANT_CHANGED_EVENT, {
      detail: { tenantId: activeTenantId },
    }));
  }
  return activeTenantId;
};

// The authenticated tenant remembered by /auth/context wins over a URL hint.
// A canonical URL remains useful for unauthenticated local/staging tools, but
// authentication always calls synchronizeBrowserTenantContext before a
// protected page mounts.
export const resolveBrowserTenantId = () => {
  if (typeof window === 'undefined') return null;
  if (isSupportTab()) return canonicalTenantId(readSupportSession()?.tenant_id);
  const urlTenant = new URLSearchParams(window.location.search).get('tenant_id');
  const rememberedTenant = localStorage.getItem(BROWSER_TENANT_STORAGE_KEY);
  const rememberedCanonical = canonicalTenantId(rememberedTenant);
  const urlCanonical = canonicalTenantId(urlTenant);
  if (rememberedTenant && !rememberedCanonical) {
    localStorage.removeItem(BROWSER_TENANT_STORAGE_KEY);
  }
  if (rememberedCanonical) return rememberedCanonical;
  if (urlCanonical) {
    localStorage.setItem(BROWSER_TENANT_STORAGE_KEY, urlCanonical);
    return urlCanonical;
  }
  return null;
};

export const tenantAwareConfig = (config = {}) => {
  const tenantId = resolveBrowserTenantId();
  if (!tenantId) return config;
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      'X-Tenant-ID': tenantId,
    },
  };
};

// Tenant and authentication are transport concerns, so every API request goes
// through this single boundary. Endpoint wrappers cannot accidentally omit it.
export const attachRequestContext = (config) => {
  if (isSupportTab()) {
    const base = new URL(API, window.location.origin);
    const target = new URL(config.url || '', `${(config.baseURL || API).replace(/\/$/, '')}/`);
    if (target.origin !== base.origin) throw new Error('Support credentials cannot be sent to another origin');
    const session = readSupportSession();
    config.headers = config.headers || {};
    for (const key of Object.keys(config.headers)) {
      if (['authorization', 'x-api-key', 'x-account-id', 'x-actionweave-control-plane-auth', 'x-aw-signed-envelope', 'x-tenant-id', 'x-aw-support-session'].includes(key.toLowerCase())) {
        delete config.headers[key];
      }
    }
    config.headers['X-AW-Support-Session'] = session?.support_token || 'ended';
    if (session?.tenant_id) config.headers['X-Tenant-ID'] = session.tenant_id;
    config.withCredentials = false;
    config.adapter = 'fetch';
    config.fetchOptions = { credentials: 'omit', redirect: 'error', cache: 'no-store', referrerPolicy: 'no-referrer' };
    return config;
  }
  const tenantId = resolveBrowserTenantId();
  config.headers = config.headers || {};
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const formatValidationIssue = (issue) => {
  if (!issue || typeof issue !== 'object') return null;
  const message = issue.msg || issue.message;
  if (!message) return null;
  const field = Array.isArray(issue.loc)
    ? issue.loc
      .filter((part) => !['body', 'query', 'path', 'header'].includes(String(part)))
      .map((part) => String(part).replaceAll('_', ' '))
      .join(' → ')
    : '';
  const fieldLabel = field
    ? field.replace(/\b\w/g, (character) => character.toUpperCase())
    : '';
  return fieldLabel ? `${fieldLabel}: ${message}` : message;
};

/**
 * Convert FastAPI, connector, and network failures into customer-readable
 * text without discarding the structured response used for diagnostics.
 */
export const getApiErrorMessage = (error, fallback = 'Request failed') => {
  let responseData = error?.response?.data;
  // Binary downloads also receive JSON errors (including mailbox-access
  // denials). Axios preserves responseType on failure, so decode only a
  // bounded JSON error; never interpret document bytes as a message.
  if (responseData instanceof ArrayBuffer) {
    const headers = error?.response?.headers;
    const mime = String(headers?.get?.('content-type') || headers?.['content-type'] || '').split(';')[0].trim().toLowerCase();
    if (responseData.byteLength <= 65536 && (mime === 'application/json' || /^application\/[\w.+-]+\+json$/.test(mime))) {
      try { responseData = JSON.parse(new TextDecoder().decode(responseData)); }
      catch { responseData = null; }
    }
  }
  const detail = responseData?.detail
    ?? responseData?.message
    ?? responseData?.error;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const issues = detail.map(formatValidationIssue).filter(Boolean);
    if (issues.length > 0) return issues.join('; ');
  }
  if (detail && typeof detail === 'object') {
    const message = detail.message || detail.detail || detail.error;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (!error?.response && typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

api.interceptors.request.use(attachRequestContext);

// Redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isSupportTab()) {
      if (error.response?.status === 401) endSupportTab();
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      localStorage.removeItem('session_token');
      localStorage.removeItem(BROWSER_TENANT_STORAGE_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Entities
export const getEntities = (params) => api.get('/entities', { params });
export const getEntity = (id) => api.get(`/entities/${id}`);
export const createEntity = (data) => api.post('/entities', data);
export const deleteEntity = (id) => api.delete(`/entities/${id}`);

// Entity Schemas (Custom Entity Builder)
export const getEntitySchemas = (params) => api.get('/entity-schemas', { params });
export const getEntitySchema = (id) => api.get(`/entity-schemas/${id}`);
export const createEntitySchema = (data) => api.post('/entity-schemas', data);
export const updateEntitySchema = (id, data) => api.put(`/entity-schemas/${id}`, data);
export const deleteEntitySchema = (id) => api.delete(`/entity-schemas/${id}`);
export const validateEntityData = (schemaId, data) => api.post(`/entity-schemas/${schemaId}/validate`, data);
export const getFieldTemplates = () => api.get('/entity-schemas/templates/fields');

// Connectors
export const getConnectorTemplates = () => api.get('/connectors/templates');
export const getConnectors = (params) => api.get('/connectors', { params });
export const getConnector = (id) => api.get(`/connectors/${id}`);
export const createConnector = (data) => api.post('/connectors', data);
export const updateConnector = (id, data) => api.put(`/connectors/${id}`, data);
export const deleteConnector = (id) => api.delete(`/connectors/${id}`);
export const testConnector = (id) => api.post(`/connectors/${id}/test`);
export const disconnectConnector = (id) => api.post(`/connectors/${id}/disconnect`);

// Registry-backed enterprise connector control plane
export const getEnterpriseConnectorDefinitions = () =>
  api.get('/v1/enterprise-connectors/definitions');
export const getEnterpriseConnectors = () => api.get('/v1/enterprise-connectors');
export const createEnterpriseConnector = (data) =>
  api.post('/v1/enterprise-connectors', data);
export const updateEnterpriseConnector = (id, data) =>
  api.put(`/v1/enterprise-connectors/${id}`, data);
export const probeEnterpriseConnector = (id) =>
  api.post(`/v1/enterprise-connectors/${id}/probe`);
export const disconnectEnterpriseConnector = (id) =>
  api.post(`/v1/enterprise-connectors/${id}/disconnect`);
export const deleteEnterpriseConnector = (id) =>
  api.delete(`/v1/enterprise-connectors/${id}`);
export const discoverEnterpriseConnector = (id, mode = 'full') =>
  api.post(`/v1/enterprise-connectors/${id}/discover`, { mode });
export const getEnterpriseConnectorResources = (id, reviewState = null) =>
  api.get(`/v1/enterprise-connectors/${id}/resources`, {
    params: reviewState ? { review_state: reviewState } : {},
  });
export const reviewEnterpriseConnectorResource = (id, resourceKind, externalId, data) =>
  api.put(
    `/v1/enterprise-connectors/${id}/resources/${encodeURIComponent(resourceKind)}/${encodeURIComponent(externalId)}/review`,
    data,
  );
export const getEnterpriseConnectorCapabilities = (id) =>
  api.get(`/v1/enterprise-connectors/${id}/capabilities`);
export const getEnterpriseConnectorSavedQueries = (id) =>
  api.get(`/v1/enterprise-connectors/${id}/saved-queries`);
export const registerEnterpriseConnectorSavedQuery = (id, data) =>
  api.post(`/v1/enterprise-connectors/${id}/saved-queries`, data);
export const deleteEnterpriseConnectorSavedQuery = (id, queryId) =>
  api.delete(`/v1/enterprise-connectors/${id}/saved-queries/${encodeURIComponent(queryId)}`);
export const getEnterpriseConnectorApiSpecs = (id) =>
  api.get(`/v1/enterprise-connectors/${id}/api-specs`);
export const registerEnterpriseConnectorApiSpec = (id, data) =>
  api.post(`/v1/enterprise-connectors/${id}/api-specs`, data);
export const deleteEnterpriseConnectorApiSpec = (id, specId) =>
  api.delete(`/v1/enterprise-connectors/${id}/api-specs/${encodeURIComponent(specId)}`);
export const bindEnterpriseSkillCapability = (data) =>
  api.post('/v1/enterprise-connectors/skill-bindings', data);
export const getEnterpriseSkillCapabilityBindings = (params = {}) =>
  api.get('/v1/enterprise-connectors/skill-bindings', { params });
export const deleteEnterpriseSkillCapabilityBinding = (bindingId) =>
  api.delete(`/v1/enterprise-connectors/skill-bindings/${encodeURIComponent(bindingId)}`);

// Events (Incoming Triggers)
export const getEvents = (params) => api.get('/events', { params });
export const getEvent = (id) => api.get(`/events/${id}`);
export const createEvent = (data) => api.post('/events', data);
export const getEventTypes = () => api.get('/events/types');
export const getEventTypesForConnector = (connectorType) => api.get(`/events/types/connector/${connectorType}`);

// Connector Events (Available Actions)
export const getConnectorEventDefinitions = (connectorType = null) => 
  api.get('/connector-events/definitions', { params: connectorType ? { connector_type: connectorType } : {} });
export const getConnectorEvents = (params) => api.get('/connector-events', { params });
export const getConnectorEventsByConnector = (connectorId) => api.get(`/connector-events/by-connector/${connectorId}`);
export const getConnectorEventCategories = () => api.get('/connector-events/categories');
export const executeConnectorEvent = (eventId, connectorId, params) => 
  api.post(`/connector-events/${eventId}/execute`, params, { params: { connector_id: connectorId } });
export const getConnectorEventLogs = (params) => api.get('/connector-events/logs', { params });

// Agents
export const getAgents = (params) => api.get('/agents', { params });
export const getAgent = (id) => api.get(`/agents/${id}`);
export const createAgent = (data) => api.post('/agents', data);
export const updateAgent = (id, data) => api.put(`/agents/${id}`, data);
export const publishAgent = (id) => api.post(`/agents/${id}/publish`);
export const deleteAgent = (id) => api.delete(`/agents/${id}`);
export const testAgent = (id, eventData) => api.post(`/agents/${id}/test`, eventData);

// Triggers
export const getTriggers = (params) => api.get('/triggers', { params });
export const getTrigger = (id) => api.get(`/triggers/${id}`);
export const createTrigger = (data) => api.post('/triggers', data);
export const toggleTrigger = (id) => api.put(`/triggers/${id}/toggle`);
export const deleteTrigger = (id) => api.delete(`/triggers/${id}`);
export const evaluateTriggers = (eventData) => api.post('/triggers/evaluate', eventData);

// Multi-Agent Planning
export const getMultiAgentPlans = (params) => api.get('/multi-agent/plans', { params });
export const getMultiAgentPlan = (id) => api.get(`/multi-agent/plans/${id}`);
export const createMultiAgentPlan = (data) => api.post('/multi-agent/plans', data);
export const executeMultiAgentPlan = (id, eventId = null) => api.post(`/multi-agent/plans/${id}/execute`, null, { 
  params: eventId ? { event_id: eventId } : {} 
});
export const executeParallelActions = (runId, actionIds) => api.post(`/runs/${runId}/parallel-actions`, actionIds);

// OAuth2 Connector Auth
export const getOAuthUrl = (connectorId, redirectUri = null) => api.get(`/connectors/${connectorId}/oauth/url`, {
  params: redirectUri ? { redirect_uri: redirectUri } : {}
});
export const refreshOAuthToken = (connectorId) => api.post(`/connectors/${connectorId}/oauth/refresh`);

// Microsoft OAuth
export const startMicrosoftOAuth = (connectorId, redirectUri = null) => 
  api.post(`/connectors/${connectorId}/oauth/microsoft/start`, null, { params: redirectUri ? { redirect_uri: redirectUri } : {} });

// Slack OAuth
export const startSlackOAuth = (connectorId, redirectUri = null) => 
  api.post(`/connectors/${connectorId}/oauth/slack/start`, null, { params: redirectUri ? { redirect_uri: redirectUri } : {} });

// Platform OAuth (unified for all connectors)
export const startPlatformOAuth = (connectorId) => api.post(`/v1/connect/${connectorId}/start`);
export const refreshPlatformOAuthManifest = (connectorId) => api.post(`/v1/connect/${connectorId}/refresh`);
export const getOAuthProviders = () => api.get('/v1/connect/providers');

// Authentication
export const getCurrentUser = () => api.get('/auth/me', { withCredentials: true });
export const getAuthContext = () => api.get('/auth/context', { withCredentials: true });
export const selectActiveTenant = (tenantId) => api.post(
  '/auth/tenant/select',
  { tenant_id: tenantId },
  { withCredentials: true },
);
export const logout = () => api.post('/auth/logout', {}, { withCredentials: true });

// Connector Marketplace
export const getMarketplaceConnectors = (params) => api.get('/marketplace/connectors', { params });
export const getMarketplaceCategories = () => api.get('/marketplace/categories');
export const getFeaturedConnectors = () => api.get('/marketplace/featured');
export const installConnector = (connectorType) => api.post(`/marketplace/connectors/${connectorType}/install`);
export const getConnectorReviews = (connectorType) => api.get(`/marketplace/connectors/${connectorType}/reviews`);
export const createConnectorReview = (connectorType, data) => api.post(`/marketplace/connectors/${connectorType}/reviews`, data, { withCredentials: true });
export const submitCustomConnector = (data) => api.post('/marketplace/submissions', data, { withCredentials: true });
export const getConnectorSubmissions = (status = null) => api.get('/marketplace/submissions', { params: status ? { status } : {} });
export const reviewConnectorSubmission = (submissionId, approved, rejectionReason = '') => 
  api.put(`/marketplace/submissions/${submissionId}/review`, null, { params: { approved, rejection_reason: rejectionReason } });

// =====================================
// Governed Managed Schedule APIs
// =====================================
export const getManagedSchedules = (params) =>
  api.get('/v1/schedules', tenantAwareConfig({ params }));
export const getManagedScheduleHistory = (scheduleKey, params) =>
  api.get(
    `/v1/schedules/${encodeURIComponent(scheduleKey)}/history`,
    tenantAwareConfig({ params })
  );
export const upsertManagedSchedule = (scheduleKey, data) =>
  api.put(
    `/v1/schedules/${encodeURIComponent(scheduleKey)}`,
    data,
    tenantAwareConfig()
  );
export const cancelManagedSchedule = (scheduleKey, data) =>
  api.post(
    `/v1/schedules/${encodeURIComponent(scheduleKey)}/cancel`,
    data,
    tenantAwareConfig()
  );

// Policy-governed external event continuations
export const getContinuationSummary = () =>
  api.get('/v1/continuations/summary', tenantAwareConfig());
export const getContinuations = (params = {}) =>
  api.get('/v1/continuations', tenantAwareConfig({ params }));
export const cancelContinuation = (continuationId, data = {}) =>
  api.post(
    `/v1/continuations/${encodeURIComponent(continuationId)}/cancel`,
    data,
    tenantAwareConfig()
  );
export const getContinuationPolicies = (params = {}) =>
  api.get('/v1/continuations/policies', tenantAwareConfig({ params }));
export const putContinuationPolicy = (policyKey, data) =>
  api.put(
    `/v1/continuations/policies/${encodeURIComponent(policyKey)}`,
    data,
    tenantAwareConfig()
  );
export const disableContinuationPolicy = (policyKey) =>
  api.delete(
    `/v1/continuations/policies/${encodeURIComponent(policyKey)}`,
    tenantAwareConfig()
  );
export const getContinuationIntents = (params = {}) =>
  api.get('/v1/continuations/intents', tenantAwareConfig({ params }));
export const retryContinuationIntent = (intentId) =>
  api.post(
    `/v1/continuations/intents/${encodeURIComponent(intentId)}/retry`,
    {},
    tenantAwareConfig()
  );
export const getContinuationAlerts = (params = {}) =>
  api.get('/v1/continuations/alerts', tenantAwareConfig({ params }));
export const resolveContinuationAlert = (alertId) =>
  api.post(
    `/v1/continuations/alerts/${encodeURIComponent(alertId)}/resolve`,
    {},
    tenantAwareConfig()
  );

// Unified automation watches and OS primitive operations
export const getAutomationSummary = () =>
  api.get('/v1/automations/summary', tenantAwareConfig());
export const getAutomationItems = (params = {}) =>
  api.get('/v1/automations/items', tenantAwareConfig({ params }));
export const getAutomationWatches = (params = {}) =>
  api.get('/v1/automations/watches', tenantAwareConfig({ params }));
export const getAutomationUsage = (params = {}) =>
  api.get('/v1/automations/usage', tenantAwareConfig({ params }));
export const getAutomationPolicy = () =>
  api.get('/v1/automations/governance/policy', tenantAwareConfig());
export const forecastAutomationWatch = (data) =>
  api.post('/v1/automations/watches/forecast', data, tenantAwareConfig());
export const getAutomationWatch = (watchKey) =>
  api.get(`/v1/automations/watches/${encodeURIComponent(watchKey)}`, tenantAwareConfig());
export const getAutomationWatchHistory = (watchKey, params = {}) =>
  api.get(`/v1/automations/watches/${encodeURIComponent(watchKey)}/history`, tenantAwareConfig({ params }));
export const runAutomationWatchNow = (watchKey) =>
  api.post(`/v1/automations/watches/${encodeURIComponent(watchKey)}/run-now`, {}, tenantAwareConfig());
export const setAutomationWatchStatus = (watchKey, action, data = {}) =>
  api.post(`/v1/automations/watches/${encodeURIComponent(watchKey)}/${action}`, data, tenantAwareConfig());
export const getComputeFunctions = () =>
  api.get('/v1/compute/functions', tenantAwareConfig());
export const getOsPrimitives = () =>
  api.get('/v1/compute/os-primitives', tenantAwareConfig());
export const getPrimitiveRecipeCapabilities = () =>
  api.get('/v1/compute/recipe-capabilities', tenantAwareConfig());
export const getComputeBindings = (params = {}) =>
  api.get('/v1/compute/bindings', tenantAwareConfig({ params }));

// Runs
export const getRuns = (params) => api.get('/runs', { params });
export const getRun = (id) => api.get(`/runs/${id}`);
export const createRun = (data) => api.post('/runs', data);
export const executeRun = (id) => api.post(`/runs/${id}/execute`);
export const executeRunWithLLM = (id) => api.post(`/runs/${id}/execute-llm`);
export const approveAction = (runId, actionId) => api.post(`/runs/${runId}/actions/${actionId}/approve`);
export const rejectAction = (runId, actionId) => api.post(`/runs/${runId}/actions/${actionId}/reject`);
export const cancelRun = (id) => api.post(`/runs/${id}/cancel`);

// Legacy (unused)
// export const getOldApprovals = (params) => api.get('/approvals', { params });

// Seed data
export const seedData = () => api.post('/seed');

// =====================================
// Dynamic Orchestration APIs
// =====================================

// Memory Layer
export const createMemoryEntry = (data) => api.post('/memory', data);
export const getWorkflowMemory = (workflowId, params) => api.get(`/memory/workflow/${workflowId}`, { params });
export const getMemoryByKey = (workflowId, key) => api.get(`/memory/workflow/${workflowId}/key/${key}`);
export const updateMemoryEntry = (memoryId, value) => api.put(`/memory/${memoryId}`, value);
export const clearWorkflowMemory = (workflowId) => api.delete(`/memory/workflow/${workflowId}`);

// Enhanced Agent Config (Goals & Constraints)
export const createAgentConfig = (agentId, data) => api.post(`/agents/${agentId}/config`, data);
export const getAgentConfig = (agentId) => api.get(`/agents/${agentId}/config`);

// Dynamic Workflow Orchestration
export const triggerOrchestration = (eventId, agentId) => 
  api.post('/orchestrate/trigger', null, { params: { event_id: eventId, agent_id: agentId } });
export const getDynamicWorkflows = (params) => api.get('/orchestrate/workflows', { params });
export const getDynamicWorkflow = (workflowId) => api.get(`/orchestrate/workflows/${workflowId}`);
export const approveWorkflow = (workflowId) => api.post(`/orchestrate/workflows/${workflowId}/approve`);
export const executeWorkflowStep = (workflowId, stepIndex = null) => 
  api.post(`/orchestrate/workflows/${workflowId}/execute-step`, null, { params: stepIndex !== null ? { step_index: stepIndex } : {} });
export const executeAllWorkflowSteps = (workflowId) => api.post(`/orchestrate/workflows/${workflowId}/execute-all`);

// =====================================
// Analytics & Reporting APIs
// =====================================
export const getAnalyticsOverview = () => api.get('/analytics/overview');
export const getWorkflowAnalytics = (params) => api.get('/analytics/workflows', { params });
export const getAgentAnalytics = () => api.get('/analytics/agents');
export const getEventAnalytics = (params) => api.get('/analytics/events', { params });
export const getMemoryAnalytics = () => api.get('/analytics/memory');
export const getPerformanceAnalytics = (params) => api.get('/analytics/performance', { params });

// =====================================
// NEW ORCHESTRATOR APIs
// =====================================

// Triggers
export const createOrchestratorTrigger = (data) => api.post('/v1/orchestrator/trigger', data, tenantAwareConfig());
export const createOrchestratorTriggerAsync = (data) => api.post('/v1/orchestrator/trigger/async', data, tenantAwareConfig());
export const getOrchestratorTriggers = (params) => api.get('/v1/orchestrator/triggers', tenantAwareConfig({ params }));
export const getOrchestratorTrigger = (id) => api.get(`/v1/orchestrator/triggers/${id}`, tenantAwareConfig());

// Executions
export const getOrchestratorExecutions = (params) => api.get('/v1/orchestrator/executions', tenantAwareConfig({ params }));
export const getOrchestratorExecution = (id) => api.get(`/v1/orchestrator/executions/${id}`, tenantAwareConfig());
export const retryOrchestratorExecution = (id, data = {}) => api.post(
  `/v1/orchestrator/executions/${id}/retry`,
  data,
  tenantAwareConfig(),
);
export const approveExecution = (id) => api.post(`/v1/orchestrator/executions/${id}/approve`, null, tenantAwareConfig());
export const rejectExecution = (id) => api.post(`/v1/orchestrator/executions/${id}/reject`, null, tenantAwareConfig());
export const getExecution = (id) => api.get(`/v1/orchestrator/executions/${id}`, tenantAwareConfig());

// Specialized Agents
export const getSpecializedAgents = () => api.get('/v1/orchestrator/agents');
export const getCapabilityCatalog = () => api.get('/v1/capabilities/catalog');
export const updateCapabilityPolicy = (agentType, actionId, policyState, reason = null) => api.put(
  `/v1/capabilities/${encodeURIComponent(agentType)}/${encodeURIComponent(actionId)}/policy`,
  { policy_state: policyState, reason },
);

// RPA as first-class Actions
export const getRpaConnections = () => api.get('/v1/rpa/connections', tenantAwareConfig());
export const createRpaConnection = (data) => api.post('/v1/rpa/connections', data, tenantAwareConfig());
export const discoverRpaWorkflows = (connectionId) => api.post(
  `/v1/rpa/connections/${encodeURIComponent(connectionId)}/discover`,
  null,
  tenantAwareConfig(),
);
export const getRpaWorkflows = () => api.get('/v1/rpa/workflows', tenantAwareConfig());
export const testRpaWorkflow = (workflowId, data) => api.post(
  `/v1/rpa/workflows/${encodeURIComponent(workflowId)}/test`,
  data,
  tenantAwareConfig(),
);
export const certifyRpaWorkflow = (workflowId) => api.post(
  `/v1/rpa/workflows/${encodeURIComponent(workflowId)}/certify`,
  null,
  tenantAwareConfig(),
);
export const getRpaRuns = (params = {}) => api.get('/v1/rpa/runs', tenantAwareConfig({ params }));
export const getRpaRun = (runId) => api.get(`/v1/rpa/runs/${encodeURIComponent(runId)}`, tenantAwareConfig());
export const acceptRpaRun = (runId) => api.post(
  `/v1/rpa/runs/${encodeURIComponent(runId)}/accept`,
  null,
  tenantAwareConfig(),
);
export const cancelRpaRun = (runId) => api.post(
  `/v1/rpa/runs/${encodeURIComponent(runId)}/cancel`,
  null,
  tenantAwareConfig(),
);
export const reviewRpaVerification = (runId, data) => api.post(
  `/v1/rpa/runs/${encodeURIComponent(runId)}/verification`,
  data,
  tenantAwareConfig(),
);
export const getRpaSchedules = () => api.get('/v1/rpa/schedules', tenantAwareConfig());
export const createRpaSchedule = (data) => api.post('/v1/rpa/schedules', data, tenantAwareConfig());
export const getRpaOperationsSummary = () => api.get('/v1/rpa/operations/summary', tenantAwareConfig());
export const retryFailedRpaCallbacks = () => api.post(
  '/v1/rpa/operations/callbacks/retry-failed',
  {},
  tenantAwareConfig(),
);
export const getSpecializedAgent = (type) => api.get(`/v1/orchestrator/agents/${type}`);
export const getAgentActionLog = (type, params) => api.get(`/v1/orchestrator/agents/${type}/actions`, { params });
export const toggleAgentEnabled = (agentType, enabled) => api.put(`/agents/${agentType}/toggle`, { enabled });

// Memory (legacy aliases)
// Use getExecutionMemory and getGlobalMemoryEntries from below

// Stats
export const getOrchestratorStats = () => api.get('/v1/orchestrator/stats', tenantAwareConfig());

// Pending Actions
export const getPendingActions = (params) => api.get('/v1/orchestrator/pending-actions', tenantAwareConfig({ params }));
export const getPendingActionStats = () => api.get('/v1/orchestrator/pending-actions/stats', tenantAwareConfig());
export const createPendingAction = (data) => api.post('/v1/orchestrator/pending-actions', data, tenantAwareConfig());
export const respondToPendingAction = (id, data) => api.post(`/v1/orchestrator/pending-actions/${id}/respond`, data, tenantAwareConfig());
export const getPendingAction = (id) => api.get(`/v1/orchestrator/pending-actions/${id}`, tenantAwareConfig());

// Approval Requests
export const getApprovals = (params) => api.get('/v1/orchestrator/approvals', tenantAwareConfig({ params }));
export const approveRequest = (id, data) => api.post(`/v1/orchestrator/approvals/${id}/approve`, data || {}, tenantAwareConfig());
export const rejectRequest = (id, data) => api.post(`/v1/orchestrator/approvals/${id}/reject`, data || {}, tenantAwareConfig());

// Demo Data
export const seedDemoData = () => api.post('/v1/orchestrator/seed-demo', null, tenantAwareConfig());
export const clearDemoData = () => api.delete('/v1/orchestrator/demo-data', tenantAwareConfig());

// Notification Routing
export const getNotificationRoutes = () => api.get('/v1/orchestrator/notification-routes', tenantAwareConfig());
export const updateNotificationRoutes = (routes) => api.put('/v1/orchestrator/notification-routes', routes, tenantAwareConfig());
export const getNotificationLog = (params) => api.get('/v1/orchestrator/notification-log', tenantAwareConfig({ params }));
export const testNotificationRouting = (data) => api.post('/v1/orchestrator/notification-routes/test', data, tenantAwareConfig());

// =====================================
// Entity Cache APIs
// =====================================
export const getEntityCache = (params) => api.get('/entity-cache', { params });
export const getEntityCacheStats = () => api.get('/entity-cache/stats');
export const getEntityCacheCategories = () => api.get('/entity-cache/categories');
export const getEntityCacheTenants = () => api.get('/entity-cache/tenants');
export const getEntityCacheItem = (id) => api.get(`/entity-cache/${id}`);
export const createEntityCacheItem = (data) => api.post('/entity-cache', data);
export const updateEntityCacheItem = (id, data) => api.put(`/entity-cache/${id}`, data);
export const deleteEntityCacheItem = (id) => api.delete(`/entity-cache/${id}`);
export const syncEntityCache = (data, demo = false) => api.post(`/entity-cache/sync?demo=${demo}`, data);
export const bulkImportEntityCache = (data) => api.post('/entity-cache/bulk-import', data);

// Entity Memory (Intelligence Layer)
export const getEntityMemories = (entityId, params) => api.get(`/entity-cache/${entityId}/memory`, { params });
export const addEntityMemory = (entityId, content, memoryType = 'insight', source = 'manual', confidence = 1.0) => 
  api.post(`/entity-cache/${entityId}/memory?content=${encodeURIComponent(content)}&memory_type=${memoryType}&source=${source}&confidence=${confidence}`);
export const searchEntityMemory = (entityId, query, limit = 5) => 
  api.get(`/entity-cache/${entityId}/memory/search`, { params: { query, limit } });
export const getEntityMemoryStats = (entityId) => api.get(`/entity-cache/${entityId}/memory/stats`);
export const deleteEntityMemory = (entityId, memoryId) => api.delete(`/entity-cache/${entityId}/memory/${memoryId}`);
export const deleteAllEntityMemories = (entityId) => api.delete(`/entity-cache/${entityId}/memory`);
export const globalMemorySearch = (query, limit = 10) => api.get('/entity-cache/memory/search', { params: { query, limit } });
export const getGlobalMemoryStats = () => api.get('/entity-cache/memory/stats');

// =====================================
// Company Context APIs
// =====================================
export const getCompanyContext = () => api.get('/company-context', tenantAwareConfig());
export const updateCompanyContext = (data) => api.put('/company-context', data, tenantAwareConfig());
export const previewCompanyPrompt = () => api.get('/company-context/prompt-preview', tenantAwareConfig());
export const autoDetectCompany = (email) => api.post(`/company-context/auto-detect?email=${encodeURIComponent(email)}`, null, tenantAwareConfig());
export const uploadCompanyLogo = (formData) => api.post('/company-context/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Operator Instructions
export const getInstructions = (limit = 50, includeExpired = false) => api.get('/company-context/instructions', tenantAwareConfig({ params: { limit, include_expired: includeExpired } }));
export const addInstruction = (text, expiresAt = null) => api.post('/company-context/instructions', { text, expires_at: expiresAt }, tenantAwareConfig());
export const deleteInstruction = (id) => api.delete(`/company-context/instructions/${id}`, tenantAwareConfig());
export const dismissInstruction = (id) => api.put(`/company-context/instructions/${id}/dismiss`, null, tenantAwareConfig());
export const reactivateInstruction = (id) => api.put(`/company-context/instructions/${id}/reactivate`, null, tenantAwareConfig());
export const clearInstructions = () => api.delete('/company-context/instructions', tenantAwareConfig());
export const sweepInstructions = () => api.post('/company-context/instructions/sweep', null, tenantAwareConfig());
export const getInstructionTags = () => api.get('/company-context/instruction-tags', tenantAwareConfig());

// Guardrail Audit Log
export const getGuardrailAuditLog = (params) => api.get('/company-context/audit-log', tenantAwareConfig({ params }));
export const getGuardrailStats = () => api.get('/company-context/audit-log/stats', tenantAwareConfig());

// New Analytics
export const getOrchestratorAnalytics = (params) => api.get('/analytics/orchestrator', { params });
export const getGuardrailAnalytics = (params) => api.get('/analytics/guardrails', { params });
export const getAgentActionAnalytics = (params) => api.get('/analytics/agent-actions', { params });

// =====================================
// Document Management APIs
// =====================================
export const getDocuments = (params) => api.get('/documents', tenantAwareConfig({ params }));
export const getDocumentStats = () => api.get('/documents/stats', tenantAwareConfig());
export const getDocumentCategories = () => api.get('/documents/categories');
export const getDocumentById = (id) => api.get(`/documents/${id}`, tenantAwareConfig());
export const createDocument = (data) => api.post('/documents', data, tenantAwareConfig());
export const updateDocument = (id, data) => api.put(`/documents/${id}`, data, tenantAwareConfig());
export const deleteDocument = (id) => api.delete(`/documents/${id}`, tenantAwareConfig());
export const getEntityDocuments = (entityId) => api.get(`/documents/entity/${entityId}/documents`, tenantAwareConfig());
export const uploadDocument = (formData) => api.post(
  '/documents/upload',
  formData,
  tenantAwareConfig(),
);
export const downloadDocument = (id) => api.get(`/documents/${id}/download`, tenantAwareConfig());

// =====================================
// SOPs & governed workflow compilation
// =====================================
export const getSopCatalog = () => api.get('/v1/sops/catalog', tenantAwareConfig());
export const getSops = (params) => api.get('/v1/sops', tenantAwareConfig({ params }));
export const getSopTemplates = () => api.get('/v1/sops/templates', tenantAwareConfig());
export const previewSopTemplate = (data) => api.post('/v1/sops/templates/preview', data, tenantAwareConfig());
export const publishSopTemplate = (data) => api.post('/v1/sops/templates', data, tenantAwareConfig());
export const applySopRetention = (data) => api.post('/v1/sops/retention', data, tenantAwareConfig());
export const adoptSopTemplate = (id, version, data) => api.post(`/v1/sops/templates/${id}/versions/${version}/adopt`, data, tenantAwareConfig());
export const getSopAlerts = () => api.get('/v1/sops/alerts', tenantAwareConfig());
export const getSopRuntimePolicy = () => api.get('/v1/sops/runtime/policy', tenantAwareConfig());
export const updateSopRuntimePolicy = (data) => api.put('/v1/sops/runtime/policy', data, tenantAwareConfig());
export const getSopUsageSources = (params) => api.get('/v1/sops/usage/reconciliation/sources', tenantAwareConfig({ params }));
export const inspectSopUsage = (kind, id) => api.get(`/v1/sops/usage/reconciliation/source/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, tenantAwareConfig());
export const reconcileSopUsage = (data) => api.post('/v1/sops/usage/reconciliation', data, tenantAwareConfig());
export const exportSopSnapshot = (id) => api.get(`/v1/sops/${id}/export`, tenantAwareConfig());
export const exportSopArchive = (id, signal) => api.get(`/v1/sops/${encodeURIComponent(id)}/archive`, tenantAwareConfig({ responseType: 'blob', timeout: 210000, signal }));
export const publishSopSkillCandidate = (id, candidateId, data) => api.post(`/v1/sops/${id}/skill-candidates/${candidateId}/publish`, data, tenantAwareConfig());
export const getSopSubmissions = (params) => api.get('/v1/sops/submissions', tenantAwareConfig({ params }));
// Heartbeats keep long model phases alive through the proxy. Axios buffers the
// small stream and retains the normal auth/tenant/error boundary. Never replay
// this POST automatically: the server may already have retained the message.
export const postSopAuthoring = async (path, data) => {
  let response;
  try {
    response = await api.post(path, data, tenantAwareConfig({
      headers: { Accept: 'text/event-stream' }, responseType: 'text',
    }));
  } catch (error) {
    // Validation/auth errors arrive before streaming starts and remain JSON.
    if (typeof error.response?.data === 'string') {
      try { error.response.data = JSON.parse(error.response.data); } catch { /* Proxy errors can be HTML. */ }
    }
    throw error;
  }
  // Rolling deployments can still answer with the existing JSON contract.
  if (response.headers?.['content-type']?.includes('application/json')) {
    return { ...response, data: typeof response.data === 'string' ? JSON.parse(response.data) : response.data };
  }
  for (const frame of String(response.data).split(/\r?\n\r?\n/)) {
    const event = frame.match(/^event: (.+)$/m)?.[1]?.trim();
    const payload = frame.match(/^data: (.+)$/m)?.[1];
    if (!payload || !['result', 'error'].includes(event)) continue;
    const value = JSON.parse(payload);
    if (event === 'result') return { ...response, data: value };
    const error = new Error('SOP authoring could not finish.');
    error.response = { status: value.status, data: { detail: value.detail } };
    throw error;
  }
  throw new Error('Lost the connection to SOP authoring. Refresh the retained conversation before retrying.');
};

export const submitSopKnowledge = (data) => postSopAuthoring('/v1/sops/intake', data);
export const getSopConversations = (params) => api.get('/v1/sops/conversations', tenantAwareConfig({ params }));
export const createSopConversation = (data) => api.post('/v1/sops/conversations', data, tenantAwareConfig());
export const getSopConversation = (id) => api.get(`/v1/sops/conversations/${id}`, tenantAwareConfig());
export const appendSopConversationMessage = (id, data) => postSopAuthoring(`/v1/sops/conversations/${id}/messages`, data);
export const getSop = (id) => api.get(`/v1/sops/${id}`, tenantAwareConfig());
export const analyzeSop = (id, data = {}) => postSopAuthoring(`/v1/sops/${id}/analyze`, data);
export const compileSop = (id, data = {}) => api.post(`/v1/sops/${id}/compile`, data, tenantAwareConfig());
export const replaySop = (id, data) => api.post(`/v1/sops/${id}/replay`, data, tenantAwareConfig());
export const historicalReplaySop = (id, data) => api.post(`/v1/sops/${id}/replay/historical`, data, tenantAwareConfig());
export const getSopRollouts = (id, params) => api.get(`/v1/sops/${id}/rollouts`, tenantAwareConfig({ params }));
export const createSopRollout = (id, data) => api.post(`/v1/sops/${id}/rollouts`, data, tenantAwareConfig());
export const transitionSopRollout = (id, rolloutId, data) => api.post(`/v1/sops/${id}/rollouts/${rolloutId}/transition`, data, tenantAwareConfig());
export const getSopRuns = (id, params) => api.get(`/v1/sops/${id}/runs`, tenantAwareConfig({ params }));
export const getSopRun = (id, runId) => api.get(`/v1/sops/${id}/runs/${runId}`, tenantAwareConfig());
export const approveSopRun = (id, runId, reason = '') => api.post(`/v1/sops/${id}/runs/${runId}/approve`, { reason }, tenantAwareConfig());
export const retrySopRunStep = (id, runId, stepId, reason = '') => api.post(`/v1/sops/${id}/runs/${runId}/steps/${stepId}/retry`, { reason }, tenantAwareConfig());
export const executeSopRuntimeEvent = (data) => api.post('/v1/sops/runtime/events', data, tenantAwareConfig());
export const getSopRuntimeOperations = (params) => api.get('/v1/sops/runtime/operations', tenantAwareConfig({ params }));
export const approveSop = (id, data) => api.post(`/v1/sops/${id}/approve`, data, tenantAwareConfig());
export const installSopSkillProposal = (id, proposalId) => api.post(`/v1/sops/${id}/skill-proposals/${proposalId}/install`, null, tenantAwareConfig());
export const validateSopSkillCandidate = (id, candidateId) => api.post(`/v1/sops/${id}/skill-candidates/${candidateId}/validate`, null, tenantAwareConfig());
export const testSopSkillCandidate = (id, candidateId, data = { scenarios: [] }) => postSopAuthoring(`/v1/sops/${id}/skill-candidates/${candidateId}/test`, data);
export const reviewSopSkillCandidate = (id, candidateId, data) => api.post(`/v1/sops/${id}/skill-candidates/${candidateId}/review`, data, tenantAwareConfig());
export const independentlyReviewSopSkillCandidate = (id, candidateId) => postSopAuthoring(`/v1/sops/${id}/skill-candidates/${candidateId}/review/independent`, null);
export const adoptSopSkillCandidate = (id, candidateId) => api.post(`/v1/sops/${id}/skill-candidates/${candidateId}/adopt`, null, tenantAwareConfig());
export const pauseSop = (id, reason = '') => api.post(`/v1/sops/${id}/pause`, { reason }, tenantAwareConfig());
export const resumeSop = (id, reason = '') => api.post(`/v1/sops/${id}/resume`, { reason }, tenantAwareConfig());
export const retireSop = (id, data) => api.post(`/v1/sops/${id}/retire`, data, tenantAwareConfig());

// =====================================
// System Mode APIs
// =====================================
export const getSystemMode = () => api.get('/system-mode', tenantAwareConfig());
export const setSystemMode = (mode) => api.put('/system-mode/mode', { mode }, tenantAwareConfig());
export const setActionOverride = (action, status) => api.put('/system-mode/action-override', { action, status }, tenantAwareConfig());
export const clearActionOverride = (action) => api.delete(`/system-mode/action-override/${action}`, tenantAwareConfig());
export const getActionClassifications = () => api.get('/system-mode/action-classifications', tenantAwareConfig());
export const previewModeEffects = (mode) => api.get('/system-mode/preview', tenantAwareConfig({ params: mode ? { mode } : {} }));

// LLM Model Settings
export const getLlmModels = () => api.get('/system-mode/llm-models');
export const getLlmSettings = () => api.get('/system-mode/llm-settings');
export const updateLlmSettings = (data) => api.put('/system-mode/llm-settings', data);

// API Keys
export const createApiKey = (data) => api.post('/api-keys', data);
export const listApiKeys = () => api.get('/api-keys');
export const deleteApiKey = (id) => api.delete(`/api-keys/${id}`);

// Partner OAuth Apps
export const getPartnerOAuthGuides = () => api.get('/v1/partner-oauth/guides');
export const getPartnerOAuthApps = () => api.get('/v1/partner-oauth/apps');
export const setPartnerOAuthApp = (provider, data) => api.put(`/v1/partner-oauth/apps/${provider}`, data);
export const deletePartnerOAuthApp = (provider) => api.delete(`/v1/partner-oauth/apps/${provider}`);

// Custom Agents
export const getCustomAgents = () => api.get('/v1/custom-agents');
export const createCustomAgent = (data) => api.post('/v1/custom-agents', data);
export const updateCustomAgent = (id, data) => api.put(`/v1/custom-agents/${id}`, data);
export const deleteCustomAgent = (id) => api.delete(`/v1/custom-agents/${id}`);

// Memory Layer
export const getExecutionMemory = (execId) => api.get(`/v1/orchestrator/memory/execution/${execId}`, tenantAwareConfig());
export const getGlobalMemoryEntries = (params) => api.get('/v1/orchestrator/memory/global', tenantAwareConfig({ params }));
export const deleteGlobalMemoryEntry = (key) => api.delete(`/v1/orchestrator/memory/global/${key}`);

// Skills, Directives & Activity
export const getSkillCatalog = (params) => api.get('/v1/skills/catalog', { params });
export const getSkillCatalogReadiness = (params) => api.get('/v1/skills/catalog-readiness', { params });
export const getSkillProfiles = (params) => api.get('/v1/skills/profiles', { params });
export const getSkillProfileSkills = (profileId, params) => api.get(`/v1/skills/profiles/${profileId}/skills`, { params });
export const getSkillRoles = (params) => api.get('/v1/skills/roles', { params });
// No enableSkillRole: role packs are filters, not grants. Bulk disable only.
export const disableSkillRole = (roleId, params) => api.post(`/v1/skills/roles/${roleId}/disable`, null, { params });
export const enableSkill = (skillId, params) => api.post(`/v1/skills/${skillId}/enable`, null, { params });
export const disableSkill = (skillId, params) => api.post(`/v1/skills/${skillId}/disable`, null, { params });
export const adoptSkillVersion = (skillId, params) => api.post(`/v1/skills/${skillId}/adopt`, null, { params });
export const followStableSkillVersion = (skillId, params) => api.post(`/v1/skills/${skillId}/follow-stable`, null, { params });
export const getSkillBehaviors = (skillId, params) => api.get(`/v1/skills/${skillId}/behaviors`, { params });
export const updateSkillBehaviors = (skillId, data, params) => api.put(`/v1/skills/${skillId}/behaviors`, data, { params });
export const resetSkillBehaviors = (skillId, params) => api.post(`/v1/skills/${skillId}/behaviors/reset`, null, { params });
export const getSkillBehaviorDiff = (skillId, params) => api.get(`/v1/skills/${skillId}/behaviors/diff`, { params });
export const getSkillBehaviorVersions = (skillId, params) => api.get(`/v1/skills/${skillId}/behaviors/versions`, { params });
export const skillMatchDebug = (data) => api.post('/v1/skills/match-debug', data);

// Browser runtime. The live-view URL these return is bearer-like:
// whoever holds it controls that browser session, so it must never be written
// to localStorage/sessionStorage or logged. Hold it in component state only.
// What is on that browser's screen right now — the operator's login window or
// the agent working; `mode` says which. 409 just means nothing is open, which
// is a normal poll state rather than an error.
export const getBrowserConnections = () => api.get('/v1/browser/connections', tenantAwareConfig());
export const createBrowserConnection = (data) => api.post('/v1/browser/connections', data, tenantAwareConfig());
export const completeBrowserTakeover = (connectionId) => api.post(
  `/v1/browser/connections/${connectionId}/takeover-complete`,
  null,
  tenantAwareConfig(),
);
export const getBrowserConnectionView = (connectionId) => api.get(
  `/v1/browser/connections/${connectionId}/view`,
  { ...tenantAwareConfig(), validateStatus: (status) => status === 200 || status === 409 },
);
// Completes the takeover and wakes the run that parked on the login. The note
// is free text the operator wants the agent to know; never a credential.
export const continueBrowserLogin = (connectionId, notes) => api.post(`/v1/browser/connections/${connectionId}/continue`, { notes: notes || '' }, tenantAwareConfig());

// Portal Desk. A task run is a browser Skill run started server-side; these
// add the sheet upload and the per-task rail.
export const uploadPortalDeskSheet = (file, conversationId) => {
  const data = new FormData();
  data.append('file', file);
  if (conversationId) data.append('conversation_id', conversationId);
  return api.post(
    '/v1/portal-desk/upload',
    data,
    tenantAwareConfig({ headers: { 'Content-Type': 'multipart/form-data' } }),
  );
};
export const getPortalDeskTasks = () => api.get('/v1/portal-desk/tasks', tenantAwareConfig());
export const getPortalDeskTask = (id) => api.get(`/v1/portal-desk/tasks/${id}`, tenantAwareConfig());
export const getPortalDeskCapabilities = () => api.get('/v1/portal-desk/capabilities', tenantAwareConfig());
const portalDeskIdempotencyConfig = (idempotencyKey) =>
  tenantAwareConfig({ headers: { 'Idempotency-Key': idempotencyKey } });

export const runPortalDeskTask = (id, expectedRevision, idempotencyKey) => api.post(
  `/v1/portal-desk/tasks/${id}/run`,
  { expected_revision: expectedRevision },
  portalDeskIdempotencyConfig(idempotencyKey),
);
export const clearPortalDeskTaskStatus = (id) => api.post(`/v1/portal-desk/tasks/${id}/clear`, null, tenantAwareConfig());
export const runAllPortalDeskTasks = (idempotencyKey, conversationId) => api.post(
  '/v1/portal-desk/run-all',
  { conversation_id: conversationId },
  portalDeskIdempotencyConfig(idempotencyKey),
);
export const stopAllPortalDeskTasks = () => api.post('/v1/portal-desk/stop-all', null, tenantAwareConfig());
export const clearPortalDesk = (conversationId) => api.post(
  '/v1/portal-desk/clear',
  { conversation_id: conversationId },
  tenantAwareConfig(),
);
export const getPortalDeskConversations = () => api.get('/v1/portal-desk/conversations', tenantAwareConfig());
export const createPortalDeskConversation = (title) => api.post('/v1/portal-desk/conversations', { title }, tenantAwareConfig());
export const getPortalDeskConversation = (id) => api.get(`/v1/portal-desk/conversations/${id}`, tenantAwareConfig());
export const sendPortalDeskMessage = (id, body) => api.post(`/v1/portal-desk/conversations/${id}/messages`, body, tenantAwareConfig());
export const applyPortalDeskProposal = (proposalId, run, idempotencyKey) => api.post(
  `/v1/portal-desk/proposals/${encodeURIComponent(proposalId)}/apply`,
  { run: Boolean(run) },
  portalDeskIdempotencyConfig(idempotencyKey),
);
export const setPortalDeskTaskInstructions = (taskId, instructions, expectedRevision) => api.post(
  `/v1/portal-desk/tasks/${taskId}/instructions`,
  { instructions, expected_revision: expectedRevision },
  tenantAwareConfig(),
);
export const runSelectedPortalDeskTasks = (taskIds, expectedRevisions, idempotencyKey) => api.post(
  '/v1/portal-desk/run-selected',
  { task_ids: taskIds, expected_revisions: expectedRevisions },
  portalDeskIdempotencyConfig(idempotencyKey),
);

// Portal Desk V2: one web-agent task per run; a run lives about two hours on the server.
export const startPortalDeskV2Task = (task, url) => api.post('/v1/portal-desk-v2/tasks', { task, url });
export const answerPortalDeskV2 = (runId, requestId, values) => api.post(
  `/v1/portal-desk-v2/tasks/${encodeURIComponent(runId)}/inputs/${encodeURIComponent(requestId)}`,
  { values },
);
export const cancelPortalDeskV2 = (runId) => api.post(`/v1/portal-desk-v2/tasks/${encodeURIComponent(runId)}/cancel`);
export const getPortalDeskV2Rerun = (executionId) => api.get(`/v1/portal-desk-v2/reruns/${encodeURIComponent(executionId)}`);

// The run's events as they happen, from the first one. EventSource cannot send the
// Authorization and X-Tenant-ID headers, so this reads the SSE response with fetch,
// and reconnects from the last event it saw when the connection drops.
export const streamPortalDeskV2 = async (runId, onEvent, signal) => {
  let lastId = '0';
  const terminal = new Set(['completed', 'failed', 'cancelled']);
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const { headers, fetchOptions } = attachRequestContext({ headers: { Accept: 'text/event-stream', 'Last-Event-ID': lastId } });
    const response = await fetch(`${API}/v1/portal-desk-v2/tasks/${encodeURIComponent(runId)}/events`, {
      headers, credentials: 'include', ...fetchOptions, signal,
    });
    if (response.status === 404) throw new Error('This task is no longer available.');
    if (!response.ok || !response.body) throw new Error(`Could not follow the task (${response.status}).`);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let end;
        while ((end = buffer.indexOf('\n\n')) >= 0) {
          const frame = buffer.slice(0, end);
          buffer = buffer.slice(end + 2);
          const fields = {};
          for (const line of frame.split('\n')) {
            const colon = line.indexOf(':');
            if (colon > 0) fields[line.slice(0, colon)] = line.slice(colon + 1).trim();
          }
          if (!fields.event) continue; // keepalive comment
          if (fields.id) lastId = fields.id;
          onEvent(fields.event, fields.data ? JSON.parse(fields.data) : {});
          if (terminal.has(fields.event)) return;
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  throw new Error('Lost the connection to the task.');
};

export const getSkillDirectives = (skillId, params) => api.get(`/v1/skills/${skillId}/directives`, { params });
export const getEffectiveSkillDirectives = (skillId, params) => api.get(`/v1/skills/${skillId}/effective-directives`, { params });
export const addSkillDirective = (skillId, data) => api.post(`/v1/skills/${skillId}/directives`, data);
export const getRoleDirectives = (roleId, params) => api.get(`/v1/roles/${roleId}/directives`, { params });
export const addRoleDirective = (roleId, data) => api.post(`/v1/roles/${roleId}/directives`, data);
export const getGlobalDirectives = (params) => api.get('/v1/directives/global', { params });
export const getPolicyDirectives = (params) => api.get('/v1/directives/policy', { params });
export const addGlobalDirective = (data) => api.post('/v1/directives/global', data);
export const editDirective = (directiveId, data) => api.patch(`/v1/directives/${directiveId}`, data);
export const setDirectiveEnabled = (directiveId, data) => api.post(`/v1/directives/${directiveId}/enable`, data);
export const removeDirective = (directiveId, params) => api.delete(`/v1/directives/${directiveId}`, { params });
export const getSkillActivity = (params) => api.get('/v1/activity', { params });
export const getSkillReportSummary = (params) => api.get('/v1/skills/reports/summary', { params });
export const getSkillRuns = (params) => api.get('/v1/skills/runs', { params });
export const getSkillRunTrace = (executionId, params) => api.get(`/v1/skills/runs/${executionId}/trace`, { params });
export const updateSkillSettings = (skillId, data, params) => api.patch(`/v1/skills/${skillId}/settings`, data, { params });
export const updateSkillModels = (skillId, data, params) => api.patch(`/v1/skills/${skillId}/models`, data, { params });

// Operations-first human plane
export const getOperationsSummary = () => api.get('/v1/operations/summary');
export const getOperationsToday = (params, options = {}) => api.get('/v1/operations/today', { ...options, params });
export const getOperationsTodayQueue = (params, options = {}) => api.get('/v1/operations/today/queue', { ...options, params });
export const getOperationsTodayOutcomes = (params, options = {}) => api.get('/v1/operations/today/outcomes', { ...options, params });
export const getAttentionQueue = (params) => api.get('/v1/operations/attention', { params });
export const getAttentionItem = (id) => api.get(`/v1/operations/attention/${id}`);
export const updateAttentionItem = (id, data) => api.patch(`/v1/operations/attention/${id}`, data);
export const getOperationsSetupIssues = (params) => api.get('/v1/operations/setup-issues', { params });
export const issueAttentionConfirmation = (id, data) => api.post(`/v1/operations/attention/${id}/confirmation`, data);
export const performAttentionAction = (id, data) => api.post(`/v1/operations/attention/${id}/act`, data);
export const getOperationsHealth = () => api.get('/v1/operations/health');
export const getOperationsActivity = (params) => api.get('/v1/operations/activity', { params });
export const getOperationsEntities = (params) => api.get('/v1/operations/entities', { params });
export const getOperationsRunStory = (executionId) => api.get(`/v1/operations/runs/${executionId}/story`);
export const getOperationsActiveRuns = (params) => api.get('/v1/operations/active-runs', { params });
export const getOperationsFunctions = (params) => api.get('/v1/operations/functions', { params });
export const saveOperationsFunctionSettings = (id, data) => api.put(`/v1/operations/functions/${encodeURIComponent(id)}/settings`, data);
export const getOperationsMode = () => api.get('/v1/operations/mode');
export const getWorkflowConversations = (params) => api.get('/v1/operations/conversations', { params });
export const getWorkflowConversationTrace = (id) => api.get(`/v1/operations/conversations/${id}`);
export const closeWorkflowConversation = (id, data) => api.post(`/v1/operations/conversations/${id}/close`, data);
export const getWorkflowConversationReviews = (params) => api.get('/v1/operations/conversation-reviews', { params });
export const resolveWorkflowConversationReview = (id, data) => api.post(`/v1/operations/conversation-reviews/${id}/resolve`, data);
export const getWorkflowConversationHealth = () => api.get('/v1/operations/conversation-health');
export const getWorkflowConversationRoutingPolicies = () => api.get('/v1/operations/conversation-routing-policies');
export const updateWorkflowConversationRoutingPolicy = (data) => api.put('/v1/operations/conversation-routing-policies', data);
export const getWorkflowConversationModelRollouts = () => api.get('/v1/operations/conversation-model-rollouts');
export const updateWorkflowConversationModelRollout = (data) => api.put('/v1/operations/conversation-model-rollouts', data);
export const deleteWorkflowConversationModelRollout = (params) => api.delete('/v1/operations/conversation-model-rollouts', { params });
export const retryWorkflowConversationObservation = (id) => api.post(`/v1/operations/conversation-observations/${id}/retry`);
export const dismissWorkflowConversationObservation = (id, data) => api.post(`/v1/operations/conversation-observations/${id}/dismiss`, data);
export const assignAttentionItem = (id, data) => api.patch(`/v1/operations/attention/${id}/assignment`, data);
export const getOperationsRunRules = (id) => api.get(`/v1/operations/runs/${id}/rules`);
export const previewOperationsRunRule = (id, data) => api.post(`/v1/operations/runs/${id}/rules/preview`, data);
export const applyOperationsRunRule = (id, data) => api.post(`/v1/operations/runs/${id}/rules`, data);
export const previewOperationsCommand = (data) => api.post('/v1/operations/commands/preview', data);
export const executeOperationsCommand = (data) => api.post('/v1/operations/commands/execute', data);
export const getOperationsBriefings = () => api.get('/v1/operations/briefings');
export const getOperationsBriefingPreferences = () => api.get('/v1/operations/briefings/preferences');
export const saveOperationsBriefingPreferences = (data) => api.put('/v1/operations/briefings/preferences', data);
export const generateOperationsBriefing = (kind = 'ad_hoc') => api.post('/v1/operations/briefings/generate', { kind });
export const acknowledgeOperationsBriefing = (id) => api.post(`/v1/operations/briefings/${id}/acknowledge`);
export const getOperationsCanvases = () => api.get('/v1/operations/canvases');
export const saveOperationsCanvas = (data) => api.put('/v1/operations/canvases', data);
export const pinOperationsCanvas = (id, data) => api.post(`/v1/operations/canvases/${id}/pin`, data);
export const shareOperationsCanvas = (id, data) => api.post(`/v1/operations/canvases/${id}/share`, data);
export const deleteOperationsCanvas = (id) => api.delete(`/v1/operations/canvases/${id}`);
export const uploadOperationsDocument = (file) => {
  const data = new FormData();
  data.append('file', file);
  data.append('name', file.name);
  // Document categories are a persistence contract, not an Operations UI
  // domain label. Use the generic supported category for ad-hoc assistant
  // attachments so the backend can store the document before orchestration.
  data.append('category', 'attachment');
  return api.post('/documents/upload', data);
};
export const queryOperationsAssistant = (data) => api.post('/v1/operations/assistant/query', data);
const SKILL_WORKSPACE_DIRECTORY_TTL_MS = 15_000;
export const SKILL_WORKSPACES_CHANGED_EVENT = 'actionweave:skill-workspaces-changed';
// No response cache: each shell mount/expansion checks current read permissions.
export const getPinnedSkillWorkspaces = (config = {}) => api.get(
  '/v1/operations/skill-workspaces', { ...config, params: { pinned_only: true } },
);
let skillWorkspaceDirectoryCache = {
  tenantId: null,
  expiresAt: 0,
  response: null,
  request: null,
};

export const invalidateSkillWorkspacesCache = () => {
  skillWorkspaceDirectoryCache = {
    tenantId: null,
    expiresAt: 0,
    response: null,
    request: null,
  };
};

export const getSkillWorkspaces = ({ force = false } = {}) => {
  const tenantId = resolveBrowserTenantId() || '';
  const now = Date.now();
  const sameTenant = skillWorkspaceDirectoryCache.tenantId === tenantId;
  if (!force && sameTenant && skillWorkspaceDirectoryCache.response
      && skillWorkspaceDirectoryCache.expiresAt > now) {
    return Promise.resolve(skillWorkspaceDirectoryCache.response);
  }
  if (!force && sameTenant && skillWorkspaceDirectoryCache.request) {
    return skillWorkspaceDirectoryCache.request;
  }
  const request = api.get('/v1/operations/skill-workspaces')
    .then((response) => {
      if (skillWorkspaceDirectoryCache.request === request) {
        skillWorkspaceDirectoryCache = {
          tenantId,
          expiresAt: Date.now() + SKILL_WORKSPACE_DIRECTORY_TTL_MS,
          response,
          request: null,
        };
      }
      return response;
    })
    .catch((error) => {
      if (skillWorkspaceDirectoryCache.request === request) {
        skillWorkspaceDirectoryCache.request = null;
      }
      throw error;
    });
  skillWorkspaceDirectoryCache = {
    tenantId,
    expiresAt: 0,
    response: null,
    request,
  };
  return request;
};
// Absolute so the browser can open it in a new tab; the API streams the document under the
// caller's tenant scope rather than handing out a signed bucket URL.
export const checkSkillDirectiveMappingConflicts = (skillId, mapping) =>
  api.post(`/v1/skills/${encodeURIComponent(skillId)}/directives/mapping-conflicts`, { mapping });

export const previewSkillDirectiveMapping = (skillId, version, mapping) =>
  api.post(
    `/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/directives/preview`,
    mapping,
    { params: { version } },
  );

// Fetched through the API client, never linked to directly: a plain <iframe src> or <a href> is a
// browser navigation that carries neither the Authorization header nor X-Tenant-ID, so it 401s
// wherever the SPA and API are not same-origin, and resolves the caller's *default* tenant for a
// user who has switched -- which then 404s because the document belongs to the other tenant.
export const getSkillWorkspaceDocumentBytes = (documentId, part) =>


  api.get(`/v1/operations/skill-workspaces/documents/${encodeURIComponent(documentId)}`, {
    params: part ? { part } : {},
    responseType: "arraybuffer",
  });

// Requested only when the documents panel is shown: expanding a container reads the whole file,
// which in production means a GCS download.
export const getSkillWorkspaceDocumentParts = (documentId) =>
  api.get(`/v1/operations/skill-workspaces/documents/${encodeURIComponent(documentId)}/parts`);

export const getSkillWorkspaceDocumentText = (documentId, part) =>
  api.get(`/v1/operations/skill-workspaces/documents/${encodeURIComponent(documentId)}/text`,
    { params: part ? { part } : {} });

export const getSkillWorkspace = (skillId, version = 'v1') => api.get(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}`, { params: { version } });
export const getSkillWorkspaceBootstrap = (skillId, version = 'v1') => api.get(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/bootstrap`, { params: { version } });
export const querySkillWorkspaceDataset = (skillId, version, resolver, params = {}) => api.post(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/datasets/query`, { resolver, params }, { params: { version } });
export const saveSkillWorkspacePreferences = (skillId, version, data) => api
  .put(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/preferences`, data, { params: { version } })
  .then((response) => {
    invalidateSkillWorkspacesCache();
    window.dispatchEvent(new CustomEvent(SKILL_WORKSPACES_CHANGED_EVENT));
    return response;
  });
export const getSkillWorkspaceRevisions = (skillId, version) => api.get(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/revisions`, { params: { version } });
export const refreshSkillWorkspaceLearning = (skillId, version) => api.post(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/learning/refresh`, null, { params: { version } });
export const generateSkillWorkspaceCandidate = (skillId, version, data = {}) => api.post(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/candidates`, data, { params: { version } });
export const publishSkillWorkspaceCandidate = (skillId, version, manifestId) => api.post(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/publish`, { manifest_id: manifestId }, { params: { version } });
export const rollbackSkillWorkspace = (skillId, version, revision) => api.post(`/v1/operations/skill-workspaces/${encodeURIComponent(skillId)}/rollback`, { revision }, { params: { version } });

// Business Obligations and Operational Recipes
export const getBusinessObligations = (params) => api.get('/v1/business-obligations', { params });
export const getBusinessObligationSummary = () => api.get('/v1/business-obligations/summary');
export const getBusinessObligationRuntime = () => api.get('/v1/business-obligations/runtime');
export const getBusinessObligation = (id) => api.get(`/v1/business-obligations/${id}`);
export const previewBusinessObligation = (data) => api.post('/v1/business-obligations/preview', data);
export const recheckBusinessObligation = (id) => api.post(`/v1/business-obligations/${id}/recheck`);
export const bulkBusinessObligations = (data) => api.post('/v1/business-obligations/bulk', data);
export const setBusinessObligationPlanStatus = (planId, action, data) => api.post(`/v1/business-obligation-plans/${planId}/${action}`, data);
export const getBusinessObligationRecipes = (params) => api.get('/v1/operational-recipes', { params });
export const getBusinessRules = (params) => api.get('/v1/business-rules', { params });
export const previewBusinessRule = (data) => api.post('/v1/business-rules/preview', data);
export const saveBusinessRule = (ruleKey, data) => api.put(`/v1/business-rules/${ruleKey}`, data);
export const getBusinessRuleRevisions = (ruleId) => api.get(`/v1/business-rules/${ruleId}/revisions`);
export const rollbackBusinessRule = (ruleId, data) => api.post(`/v1/business-rules/${ruleId}/rollback`, data);
export const getRecipeVersions = (params) => api.get('/v1/recipe-control/versions', { params });
export const getRecipeReliability = (params) => api.get('/v1/recipe-control/reliability', { params });
export const getRecipeRollouts = () => api.get('/v1/recipe-control/rollouts');
export const getRecipePlatformAccess = () => api.get('/v1/recipe-control/platform/access');
export const saveRecipeDraft = (data) => api.post('/v1/recipe-control/platform/drafts', data);
export const transitionRecipeVersion = (recipeId, version, action, data) => api.post(`/v1/recipe-control/platform/${recipeId}/${version}/${action}`, data);
export const previewRecipeRollout = (data) => api.post('/v1/recipe-control/rollouts/preview', data);
export const createRecipeRollout = (data) => api.post('/v1/recipe-control/rollouts', data);
export const changeRecipeRollout = (id, action, data) => api.post(`/v1/recipe-control/rollouts/${id}/state/${action}`, data);
export const applyRecipeRollout = (id, limit = 100) => api.post(`/v1/recipe-control/rollouts/${id}/apply`, null, { params: { limit } });
export const replayRecipeVersion = (data) => api.post('/v1/recipe-control/replay', data);
export const recordRecipeFeedback = (data) => api.post('/v1/recipe-control/feedback', data);
export const generateSkillObligationMappings = () => api.post('/v1/skill-obligation-mappings/generate');
export const getSkillObligationMappings = (params) => api.get('/v1/skill-obligation-mappings', { params });

// Enterprise document generation
export const getEnterpriseDocumentCatalog = () => api.get('/v1/enterprise-documents/catalog');
export const publishEnterpriseDocumentRecipe = (definition) => api.post('/v1/enterprise-documents/catalog/recipes', { definition });
export const publishEnterpriseDocumentTemplate = (definition) => api.post('/v1/enterprise-documents/catalog/templates', { definition });
export const getEnterpriseDocuments = (params) => api.get('/v1/enterprise-documents', { params });
export const getEnterpriseDocument = (draftId) => api.get(`/v1/enterprise-documents/drafts/${draftId}`);
export const createEnterpriseDocumentDraft = (payload) => api.post('/v1/enterprise-documents/drafts', payload);
export const updateEnterpriseDocumentDraft = (draftId, payload) => api.put(`/v1/enterprise-documents/drafts/${draftId}`, payload);
export const requestEnterpriseDocumentApproval = (draftId, payload = {}) => api.post(`/v1/enterprise-documents/drafts/${draftId}/approval`, payload);
export const decideEnterpriseDocumentApproval = (draftId, payload) => api.post(`/v1/enterprise-documents/drafts/${draftId}/approval/decision`, payload);
export const finalizeEnterpriseDocument = (draftId, payload) => api.post(`/v1/enterprise-documents/drafts/${draftId}/finalize`, payload);
export const deliverEnterpriseDocument = (versionId, payload) => api.post(`/v1/enterprise-documents/versions/${versionId}/deliver`, payload);
export const voidEnterpriseDocument = (versionId, payload) => api.post(`/v1/enterprise-documents/versions/${versionId}/void`, payload);
export const correctEnterpriseDocument = (versionId, payload) => api.post(`/v1/enterprise-documents/versions/${versionId}/correct`, payload);
export const retryEnterpriseDocumentDelivery = (deliveryId) => api.post(`/v1/enterprise-documents/deliveries/${deliveryId}/retry`);
export const enterpriseDocumentPreviewUrl = (draftId, format = 'html') => `${API}/v1/enterprise-documents/drafts/${draftId}/preview?format=${encodeURIComponent(format)}`;
export const enterpriseDocumentDownloadUrl = (versionId) => `${API}/v1/enterprise-documents/versions/${versionId}/download`;
export const getEnterpriseDocumentPreview = (draftId, format = 'html') => api.get(`/v1/enterprise-documents/drafts/${draftId}/preview`, { params: { format }, responseType: format === 'pdf' ? 'blob' : 'text' });
export const downloadEnterpriseDocument = (versionId) => api.get(`/v1/enterprise-documents/versions/${versionId}/download`, { responseType: 'blob' });
export const getEnterpriseDocumentBrand = () => api.get('/v1/enterprise-documents/brand');
export const saveEnterpriseDocumentBrand = (payload) => api.put('/v1/enterprise-documents/brand', payload);
export const discoverEnterpriseDocumentBrand = (payload) => api.post('/v1/enterprise-documents/brand/discover', payload);
export const uploadEnterpriseDocumentBrandAsset = (file, kind = 'logo') => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/v1/enterprise-documents/brand/assets?kind=${encodeURIComponent(kind)}`, form);
};
export const reviewSkillObligationMapping = (id, data) => api.post(`/v1/skill-obligation-mappings/${id}/review`, data);

export default api;

// --- Live Operations signals -------------------------------------------------
// The socket carries invalidation signals only, never queue rows: the client
// re-reads through the normal permission-scoped API, so a push can never widen
// what a user may see. The session token travels in the WebSocket subprotocol
// rather than the query string, because query strings are written to access logs.
export const OPERATIONS_WS_SUBPROTOCOL = 'aw-session';

export function openOperationsSocket({ onEvent, onStatus, runId = null } = {}) {
  if (isSupportTab()) { onStatus?.('polling'); return () => {}; }
  const token = localStorage.getItem('session_token');
  if (!token) return () => {};

  const httpBase = BACKEND_URL || window.location.origin;
  const wsBase = httpBase.replace(/^http/, 'ws');
  const path = runId ? `/api/ws/runs/${encodeURIComponent(runId)}` : '/api/ws/operations';

  let socket = null;
  let heartbeat = null;
  let retry = null;
  let attempt = 0;
  let closed = false;

  const connect = () => {
    if (closed) return;
    try {
      socket = new WebSocket(`${wsBase}${path}`, [OPERATIONS_WS_SUBPROTOCOL, token]);
    } catch (error) {
      schedule();
      return;
    }
    socket.onopen = () => {
      attempt = 0;
      onStatus?.('live');
      heartbeat = setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) socket.send('ping');
      }, 25000);
    };
    socket.onmessage = (message) => {
      let event = null;
      try {
        event = JSON.parse(message.data);
      } catch (error) {
        return;
      }
      if (!event || event.type === 'pong' || event.type === 'ready') return;
      onEvent?.(event);
    };
    socket.onclose = () => {
      clearInterval(heartbeat);
      onStatus?.('offline');
      schedule();
    };
    socket.onerror = () => { try { socket?.close(); } catch (error) { /* handled by onclose */ } };
  };

  const schedule = () => {
    if (closed) return;
    // Capped backoff; polling remains the fallback so a closed socket degrades
    // to a slower refresh instead of a silently stale screen.
    const delay = Math.min(30000, 1000 * 2 ** attempt++);
    retry = setTimeout(connect, delay);
  };

  connect();
  return () => {
    closed = true;
    clearInterval(heartbeat);
    clearTimeout(retry);
    try { socket?.close(); } catch (error) { /* already closed */ }
  };
}

export const getMessagingSetup = (id) => api.get(`/v1/messaging/connections/${id}`);
export const saveMessagingSetup = (id, config) => api.put(`/v1/messaging/connections/${id}`, config);
export const selectMessagingChannels = (id, channels) => api.put(`/v1/messaging/connections/${id}/channels`, channels);
export const getMessagingDirectory = (id, cursor = "", teamId = "") => api.get(`/v1/messaging/connections/${id}/directory`, { params: { cursor, team_id: teamId } });
export const createMessagingLinkCode = (id) => api.post(`/v1/messaging/connections/${id}/link-code`);
export const unlinkMessagingIdentity = (id) => api.delete(`/v1/messaging/connections/${id}/identity`);
export const getMessagingHealth = (id) => api.get(`/v1/messaging/connections/${id}/health`);
export const recoverMessagingDelivery = (id, deliveryId, resolution) => api.post(`/v1/messaging/connections/${id}/deliveries/${deliveryId}/recover`, resolution);

export const registerMessagingWebhook = (id) => api.post(`/v1/messaging/connections/${id}/register-webhook`);

export const getWorkplaceWatches = (id) => api.get(`/v1/workplace/connections/${id}/watches`);
export const saveWorkplaceWatch = (id, data) => api.put(`/v1/workplace/connections/${id}/watches`, data);

// Customer SQL bridge management; credentials remain on the Windows machine.
export const getSQLBridge = id => api.get(`/v1/enterprise-connectors/${id}/sql-bridge`);
export const getSQLBridgeEnrollment = id => api.post(`/v1/enterprise-connectors/${id}/sql-bridge/enrollment`);
export const saveSQLBridgePolicy = (id,data) => api.put(`/v1/enterprise-connectors/${id}/sql-bridge/policy`,data);
export const submitSQLBridgeOperation = (id,data) => api.post(`/v1/enterprise-connectors/${id}/sql-bridge/operations`,data);
export const getSQLBridgeOperation = (id,operation) => api.get(`/v1/enterprise-connectors/${id}/sql-bridge/operations/${operation}`);
export const getSQLBridgeEvents = id => api.get(`/v1/enterprise-connectors/${id}/sql-bridge/events`);

export const getCustomerMappings = () => api.get('/v1/sops/knowledge/mappings', tenantAwareConfig());
export const previewCustomerMappings = (entries, reference_text = '') => api.post('/v1/sops/knowledge/mappings/preview', { entries, reference_text }, tenantAwareConfig());
export const searchCustomerMappings = (q) => api.get('/v1/sops/knowledge/mappings/search', tenantAwareConfig({ params: { q } }));
export const publishCustomerMappings = (data) => api.post('/v1/sops/knowledge/mappings/publish', data, tenantAwareConfig());
export const activateCustomerMappings = (data) => api.post('/v1/sops/knowledge/mappings/activate', data, tenantAwareConfig());
export const importCustomerMappings = (file) => { const data = new FormData(); data.append('file', file); return api.post('/v1/sops/knowledge/mappings/import', data, tenantAwareConfig()); };
export const proposeCustomerMappings = (content) => api.post('/v1/sops/knowledge/mappings/propose', { content }, tenantAwareConfig({ timeout: 180000 }));
